import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import styles from './Toast.module.css'

type ToastTone = 'success' | 'error' | 'info'

interface Toast {
  id: number
  tone: ToastTone
  message: string
}

interface ToastContextValue {
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

/** Errors linger; confirmations do not. */
const DURATIONS: Record<ToastTone, number> = {
  success: 3500,
  info: 4000,
  // Long enough to read a server message twice. An error that vanishes before
  // it is read is the same as no error at all.
  error: 7000,
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const shownCount = useRef(0)

  /*
   * *** THE TOP LAYER IS ORDERED BY WHEN AN ELEMENT ENTERED IT, NOT BY z-index ***
   *
   * `popover="manual"` puts this stack in the top layer, which is what lets it
   * paint above a `<dialog>`. But entering once at mount is not enough: every
   * dialog opened AFTERWARDS enters the top layer above it. The symptom was an
   * error toast rendering behind an open modal form — so a failed save inside a
   * dialog looked like nothing happening at all.
   *
   * Re-showing whenever a toast is ADDED moves the stack back to the top, above
   * whatever dialog is currently open. Only on add: doing it on every render
   * would churn, and doing it on dismissal is pointless.
   */
  useEffect(() => {
    const node = containerRef.current
    if (!node) return

    const added = toasts.length > shownCount.current
    shownCount.current = toasts.length

    try {
      if (!node.matches(':popover-open')) {
        node.showPopover()
      } else if (added) {
        // Same tick, so there is no frame in which the toast is missing.
        node.hidePopover()
        node.showPopover()
      }
    } catch {
      // Older browsers without popover support fall back to the z-index in
      // Toast.module.css, which is correct everywhere except above a dialog.
    }
  }, [toasts.length])
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const push = useCallback(
    (tone: ToastTone, message: string) => {
      const id = nextId.current++
      setToasts((current) => [...current, { id, tone, message }])

      const timer = setTimeout(() => dismiss(id), DURATIONS[tone])
      timers.current.set(id, timer)
    },
    [dismiss],
  )

  // Clear pending timers on unmount so a dismissed toast cannot fire a state
  // update against a gone component.
  useEffect(() => {
    const pending = timers.current
    return () => {
      pending.forEach(clearTimeout)
      pending.clear()
    }
  }, [])

  const value = useMemo<ToastContextValue>(
    () => ({
      success: (message) => push('success', message),
      error: (message) => push('error', message),
      info: (message) => push('info', message),
    }),
    [push],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/*
        `aria-live="polite"` announces each toast without interrupting whatever
        the user is doing. A confirmation nobody can hear is only half a
        confirmation.
      */}
      {/*
        *** A TOAST MUST OUTRANK AN OPEN DIALOG ***
        `showModal()` puts a <dialog> in the browser's TOP LAYER, which sits
        above every z-index there is — so an error toast fired from inside a
        form was rendering behind it, invisible exactly when it mattered.

        `popover="manual"` promotes this stack into the same top layer. It is
        shown imperatively below, because a popover is hidden until told
        otherwise.
      */}
      <div
        ref={containerRef}
        popover="manual"
        className={styles.container}
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`${styles.toast} ${styles[toast.tone]}`}
            role={toast.tone === 'error' ? 'alert' : 'status'}
          >
            <span className={styles.icon} aria-hidden="true">
              {toast.tone === 'success' ? '✓' : toast.tone === 'error' ? '⚠' : 'ℹ'}
            </span>
            <p className={styles.message}>{toast.message}</p>
            <button
              type="button"
              className={styles.dismiss}
              onClick={() => dismiss(toast.id)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used inside a ToastProvider')
  }
  return context
}
